import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { Eyebrow } from "@/components/marketing/primitives";
import { FAQSection } from "@/components/marketing/FAQSection";
import { PRIVACY_FAQS, faqJsonLd } from "@/content/faqs";
import { OG_IMAGE, SITE, abs } from "@/content/siteConfig";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy: Berk Hospitality Group" },
      {
        name: "description",
        content:
          "How Berk Hospitality Group collects, uses, and protects your information. Plain English, no hidden practices.",
      },
      { property: "og:title", content: "Privacy Policy: Berk Hospitality Group" },
      {
        property: "og:description",
        content:
          "How Berk Hospitality Group collects, uses, and protects your information. Plain English, no hidden practices.",
      },
      { property: "og:url", content: abs("/privacy") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: abs("/privacy") }],
    scripts: [
      { type: "application/ld+json", children: faqJsonLd(PRIVACY_FAQS) },
    ],
  }),
  component: PrivacyPage,
});

const lastUpdated = "November 3, 2026";

function PrivacyPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Privacy"
        lede="What we collect, how we use it, and the choices you have. We keep it simple because there is nothing complicated to hide."
      >
        Privacy Policy
      </PageHero>

      <section className="bhg-section">
        <div className="wrap">
          <div className="grid gap-12 lg:grid-cols-[1fr_2.2fr]">
            <div className="hidden lg:block">
              <div className="sticky top-28">
                <Eyebrow>Contents</Eyebrow>
                <nav>
                  <ul className="space-y-3" style={{ fontSize: 13.5, color: "rgba(0,47,167,0.74)" }}>
                    <li>
                      <a href="#what-we-collect" className="hover:text-[#002FA7] transition-colors">What we collect</a>
                    </li>
                    <li>
                      <a href="#analytics" className="hover:text-[#002FA7] transition-colors">Analytics and advertising</a>
                    </li>
                    <li>
                      <a href="#cookies" className="hover:text-[#002FA7] transition-colors">Cookies</a>
                    </li>
                    <li>
                      <a href="#booking" className="hover:text-[#002FA7] transition-colors">Booking a call</a>
                    </li>
                    <li>
                      <a href="#retention" className="hover:text-[#002FA7] transition-colors">Retention</a>
                    </li>
                    <li>
                      <a href="#choices" className="hover:text-[#002FA7] transition-colors">Your choices</a>
                    </li>
                    <li>
                      <a href="#contact" className="hover:text-[#002FA7] transition-colors">Contact us</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <article>
              <p className="mb-10" style={{ fontSize: 15, color: "rgba(0,47,167,0.55)" }}>
                Last updated: {lastUpdated}
              </p>

              <div className="space-y-12">
                <section id="what-we-collect">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    What we collect
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      We only collect information that helps us respond to you or improve the site. We do not sell data, and we do not run an app, store, or user account system.
                    </p>
                    <ul className="list-disc space-y-2 pl-5">
                      <li>
                        <strong style={{ color: "#002FA7" }}>Contact form submissions.</strong> When you request an audit through our form, we collect your name, email address, phone number, and the message you write. This is used to reply to you and prepare for a call.
                      </li>
                      <li>
                        <strong style={{ color: "#002FA7" }}>Analytics data.</strong> We use Google Analytics 4 and Google Tag Manager to understand how visitors use the site. This includes pages viewed, clicks on phone and email links, form submissions, and general information such as your general location and the type of device you are using.
                        <p className="mt-3">
                          We also use Microsoft Clarity to understand how visitors use the site. It records clicks, scrolling and pages viewed, and produces session replays and heatmaps. Text you type into forms is masked in these recordings.
                        </p>
                      </li>
                      <li>
                        <strong style={{ color: "#002FA7" }}>Booking data.</strong> When you book a call through the scheduler on our contact page, your appointment details are handled by Google Calendar appointment scheduling. We do not store that information on our own servers.
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="analytics">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    Google Analytics and advertising features
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      We use Google Analytics 4 with Google signals enabled. This means Google may associate your visit data with signed-in Google users who have already consented to personalized advertising, and it supports features like cross-device reporting and advertising audiences.
                    </p>
                    <p>
                      You can learn more about how Google handles this data in{" "}
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-4 transition-colors hover:text-[#8a6f3f]"
                        style={{ color: "#002FA7" }}
                      >
                        Google's privacy policy
                      </a>
                      . If you prefer not to be tracked by Google Analytics, you can install the{" "}
                      <a
                        href="https://tools.google.com/dlpage/gaoptout"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-4 transition-colors hover:text-[#8a6f3f]"
                        style={{ color: "#002FA7" }}
                      >
                        Google Analytics opt-out browser add-on
                      </a>
                      .
                    </p>
                  </div>
                </section>

                <section id="cookies">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    Cookies
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      Google Analytics, Google Tag Manager and Microsoft Clarity may set cookies on your browser to recognize repeat visits, measure traffic, and record events like clicks and form submissions. These cookies are used for measurement and advertising audiences, not to store personal account information, because we do not operate user accounts.
                    </p>
                    <p>
                      Most browsers let you block or delete cookies through your settings. Blocking analytics cookies will not break the site, but it will stop us from seeing how you found us.
                    </p>
                  </div>
                </section>

                <section id="booking">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    Booking a call
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      The scheduling tool on our contact page is Google Calendar appointment scheduling. Anything you enter there, including your name, email, and meeting details, is submitted to and stored by Google under their privacy policy. We receive the appointment details so we can show up to the call, but we do not store them separately in our own database.
                    </p>
                  </div>
                </section>

                <section id="retention">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    Retention
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      Analytics data is retained in Google Analytics 4 for 14 months. Contact form submissions and related email correspondence are kept for as long as necessary to respond to you and follow up on business discussions, after which they are deleted or archived.
                    </p>
                  </div>
                </section>

                <section id="choices">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    Your choices
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      You can ask us to access, update, or delete the information we hold about you by emailing{" "}
                      <a
                        href={SITE.emailHref}
                        className="underline underline-offset-4 transition-colors hover:text-[#8a6f3f]"
                        style={{ color: "#002FA7" }}
                      >
                        {SITE.email}
                      </a>
                      . We will confirm your request and respond as quickly as we can, typically within a few business days.
                    </p>
                  </div>
                </section>

                <section id="contact">
                  <h2
                    className="mb-4"
                    style={{
                      fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                      fontWeight: 300,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#002FA7",
                      lineHeight: 1.2,
                    }}
                  >
                    Contact us
                  </h2>
                  <div className="space-y-4" style={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(0,47,167,0.74)" }}>
                    <p>
                      Berk Hospitality Group
                      <br />
                      Scottsdale, AZ
                      <br />
                      Email:{" "}
                      <a
                        href={SITE.emailHref}
                        className="underline underline-offset-4 transition-colors hover:text-[#8a6f3f]"
                        style={{ color: "#002FA7" }}
                      >
                        {SITE.email}
                      </a>
                    </p>
                  </div>
                </section>
              </div>
            </article>
          </div>
        </div>
      </section>
      <FAQSection items={PRIVACY_FAQS} />
    </SiteShell>
  );
}
